import { useTranslate } from 'hooks/useTranslate';
import { FC } from 'react';

import InputText from 'modules/_shared/components/InputText';
import InputError from 'modules/_shared/components/InputError';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import Button from 'modules/_shared/components/Buttons';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';

import { EncumbranceTypes } from '../constants/encumbranceOptions';
import { encumbranceCoOwnersConfig } from '../constants/encumbranceCoOwnersConfig';

import { useCoOwnerView } from '../context/CoOwnerViewContext';
import { checkIsDisabledAddCoOwnerButton } from '../utils/checkIsDisabledAddCoOwnerButton';

type Props = {
  encumbranceName: EncumbranceTypes;
};

const ShareEncumbranceViewCoOwnersForm: FC<Props> = ({ encumbranceName }) => {
  const { t } = useTranslate();

  const { data, actions } = useCoOwnerView();

  const { hasMembersSelector } = encumbranceCoOwnersConfig[encumbranceName] || {
    hasMembersSelector: false,
  };

  const isDisabledAddButton = checkIsDisabledAddCoOwnerButton({
    selectedPartner: data.selectedPartner,
    name: data.name,
    cif: data.cif,
    isValidEmail: data.isValidEmail,
    isValidPercent: data.isValidPercent,
  });

  return (
    <>
      <div className="row g-4">
        {hasMembersSelector ? (
          <div className="col-5">
            <div className="form-group">
              <label className="form-label" htmlFor="co-owner-name">
                <span className="d-flex flex-row justify-content-start">
                  {t('Shareholder')}
                  <IconTooltip
                    id="encumbrance-shareholder-tooltip"
                    tooltip={t('CoOwnerPartnerInfo')}
                  />
                </span>
              </label>

              <CustomAsyncSelect
                id="partner-select-input"
                onChange={actions.handleChangeSelectedPartner}
                onFetchData={actions.handleFetchPartnersByTerm}
                value={data.selectedPartner}
                placeholder={t('SearchPartner')}
                minimumInputLength={3}
                noDataLabelKey="NoPartnersHaveBeenFound"
                loadingLabelKey="LoadingPartners"
                errorLabelKey="ErrorLoadingPartners"
                defaultOptions={data.partnerOptions}
                className=""
              />
            </div>
          </div>
        ) : (
          <div className="col-5">
            <div className="form-group">
              <InputText
                id="co-owner-name"
                label={t('Name')}
                value={data.name}
                onChange={actions.handleChangeName}
                required
                lgSize={false}
              />
            </div>
          </div>
        )}

        <div className="col-3">
          <div className="form-group">
            <InputText
              id="co-owner-cif"
              label={t('Cif')}
              value={data.cif}
              onChange={actions.handleChangeCif}
              required
              lgSize={false}
            />
          </div>
        </div>

        <div className="col-4">
          <div className="form-group mb-0">
            <InputText
              id="co-owner-email"
              label={t('Email')}
              value={data.email}
              onChange={actions.handleChangeEmail}
              emailInput
              required
              lgSize={false}
            />
          </div>
          {!data.isValidEmail && (
            <InputError errorMessage={t('InvalidEmail')} />
          )}
        </div>

        <div className="col-2">
          <div className="form-group">
            <label className="form-label" htmlFor="co-owner-percent">
              <span className="d-flex flex-row justify-content-start">
                {t('Percentage')}
                <IconTooltip
                  id="encumbrance-percentage-tooltip"
                  tooltip={t('PercentageOverTheRangeSelected')}
                />
              </span>
            </label>
            <div className="form-control-wrap">
              <div className="form-icon form-icon-right">
                <em className="icon ni ni-percent" />
              </div>
              <input
                type="number"
                id="co-owner-percent"
                className="form-control form-control"
                value={data.percent === 0 ? '' : data.percent}
                onChange={actions.handleChangePercent}
                min={0}
                step={0.01}
                max={100}
              />
            </div>
          </div>
        </div>

        <div className="col-6">
          <div className="form-group">
            <InputText
              id="co-owner-email"
              label={t('Comment')}
              value={data.comments}
              onChange={actions.handleChangeComments}
              lgSize={false}
            />
          </div>
        </div>

        <div className="col-4 d-flex align-items-end">
          <Button
            icon="plus"
            className="btn-block"
            onClick={actions.handleSaveCoOwner}
            disabled={isDisabledAddButton}
            outlineStyle={isDisabledAddButton}
            dimStyle={isDisabledAddButton}
          >
            {t('AddEncumbranceCoOwner')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ShareEncumbranceViewCoOwnersForm;
